import { type FC, type ReactNode } from "react";
import { match } from "ts-pattern";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, Icon, ControlRow, CircularIcon, Button } from "@visioni/a3s-ui/components";
import { SpinEffect } from "@visioni/a3s-ui/animations";
import "@visioni/a3s-ui/theme";
import {
  BUTTON_LABELS,
  describe as describeView,
  describeAdjustRow,
  labelInProgress,
  type AlarmControlView,
  type ButtonLabel,
  type PendingIntentKind,
} from "../alarm-control";
import { Duration, type AlarmControlCommand } from "../alarm-control/commands";

const ICON_SIZE = 18;
const CLOCK_ICON_SIZE = 18;

// Heights, expressed via spacing tokens, used to derive the vertical
// alignment offsets below. Keeping all dimensions tokenised keeps a
// future Theme tweak from breaking the icon/title/button alignment math.
//
//   - LOCK_ICON_DIAMETER (32 = 8xl) — the CircularIcon is exactly this
//     wide and tall.
//   - CONTROL_BUTTON_HEIGHT (36 = 9xl) — taller than the icon so the
//     primary blue button has a comfortable tap target, but still
//     visually lighter than the default Button height of 44.
//   - TITLE_LINE_HEIGHT (18 = 4xl) — overrides the `md/medium` Text
//     style's default lineHeight (24px). Tightening to 18px (just 2px
//     above the 16px font size) brings the title-to-subtitle gap in
//     line with the Adjust row's own xs/regular (16px lineHeight)
//     feel — the dense card now reads as one tight stack instead of
//     two airy rows above one tight row. Used as the "centring
//     anchor" so the icon and button visually sit on the title line.
//
// Each visual element's `marginTop` lift is then `-(elementHeight -
// TITLE_LINE_HEIGHT) / 2` so its vertical centre lands on the title's
// vertical centre. (32 - 18) / 2 = 7 — there's no 7px spacing token,
// so we round to `sm` (6px). The 1px residual is below perceptual
// threshold.

/**
 * Which buttons the host has enabled. The view-model variant decides *which*
 * buttons are meaningful; these flags decide *whether* to offer them at all.
 * Matches the existing `CAMERA_ALARM_OVERRIDE` / `CAMERA_ALARM_SILENCE` /
 * `CAMERA_ALARM_ADJUST` flags used by a3s-app.
 */
export type AlarmControlFlags = {
  readonly override: boolean;
  readonly silence: boolean;
  readonly adjust: boolean;
};

export interface AlarmControlProps {
  readonly view: AlarmControlView;
  readonly onCommand: (command: AlarmControlCommand) => void;
  readonly controls: AlarmControlFlags;
}

const DEFAULT_ARM_DURATION = Duration.preset("1h");
const DEFAULT_DISARM_DURATION = Duration.preset("1h");
const DEFAULT_EXTEND_BY = Duration.preset("1h");
const DEFAULT_SILENCE_MINUTES = 15 as const;

interface PendingAwareButtonProps {
  // Typed as `ButtonLabel` so callers must use the `BUTTON_LABELS` union —
  // keeps copy and aria-label generation honest.
  readonly label: ButtonLabel;
  readonly size: "md";
  readonly variant: "primary" | "secondary";
  readonly variantColor: "blue" | "grey" | "red";
  readonly iconLeft?: ReactNode;
  readonly onPress: () => void;
  readonly pending: boolean;
  readonly testID?: string;
  /** Forwarded to both the size-reservation and overlay Buttons so they
   *  match shape (e.g. compact 32px height for in-card action buttons). */
  readonly style?: ButtonStyleOverride;
}

/** The subset of `<Button style>` we forward — extracted so consumers don't
 *  reach into reanimated/RN style internals. */
type ButtonStyleOverride = {
  readonly height?: number;
  readonly minWidth?: number;
  readonly marginTop?: number;
};

/**
 * A `<Button>` that either renders normally (`pending=false`) or keeps its
 * intrinsic width while swapping its label for a spinner (`pending=true`).
 *
 * Rendering twice — once invisibly to reserve the label's natural width,
 * once as a spinner-only button absolutely positioned on top — is the
 * least-assumptive way to meet the "no size change, no text, just a
 * spinner" requirement without patching the shared `Button` primitive or
 * hardcoding per-label widths.
 *
 * The reservation button is `pointerEvents="none"` and `aria-hidden` so it
 * doesn't receive focus or a screen-reader announcement; the visible
 * spinner button keeps the original `accessibilityLabel` so assistive
 * technology knows which command is in flight.
 */
const PendingAwareButton: FC<PendingAwareButtonProps> = ({
  label,
  size,
  variant,
  variantColor,
  iconLeft,
  onPress,
  pending,
  testID,
  style,
}) => {
  // Layout-shifting properties (marginTop) belong on the outer wrapper
  // for the pending-overlay variant, not on the inner Buttons —
  // otherwise the spinner-overlay's absolutely-positioned Button moves
  // away from the size-reservation Button. Box-model properties
  // (height) stay on the Button itself.
  const { marginTop, ...buttonShapeStyle } = style ?? {};
  const wrapperLift = marginTop !== undefined ? { marginTop } : undefined;
  if (!pending) {
    return (
      <Button
        label={label}
        size={size}
        variant={variant}
        variantColor={variantColor}
        iconLeft={iconLeft}
        onPress={onPress}
        {...(style !== undefined ? { style } : {})}
        {...(testID !== undefined ? { testID } : {})}
      />
    );
  }
  // Merge caller's *shape* (height, minWidth) with the overlay's stretch
  // styles so a compact height applies to both layers (size-reservation
  // and visible spinner). Width comes from the size-reservation button's
  // intrinsic content; height is whichever the caller asks for. The
  // marginTop is hoisted to the wrapper so the spinner overlay doesn't
  // visually drift.
  const overlayStyle = { ...pendingButtonStyles.overlayButton, ...buttonShapeStyle };
  return (
    <View style={[pendingButtonStyles.wrapper, wrapperLift]}>
      <View
        style={pendingButtonStyles.sizeReservation}
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        <Button
          label={label}
          size={size}
          variant={variant}
          variantColor={variantColor}
          iconLeft={iconLeft}
          onPress={noop}
          disabled
          style={buttonShapeStyle}
        />
      </View>
      <View style={pendingButtonStyles.overlay} pointerEvents="box-none">
        <Button
          label={label}
          size={size}
          variant={variant}
          variantColor={variantColor}
          iconLeft={iconLeft}
          onPress={noop}
          loading
          loadingText=""
          style={overlayStyle}
          accessibilityLabel={labelInProgress(label)}
          {...(testID !== undefined ? { testID } : {})}
        />
      </View>
    </View>
  );
};

const noop = () => undefined;

const pendingButtonStyles = StyleSheet.create({
  // The wrapper inherits whatever alignment the parent uses (e.g. the
  // silence row stretches, the primary row's action slot shrinks to
  // intrinsic). The size-reservation Button underneath determines the
  // wrapper's height and (when the parent doesn't stretch) width.
  wrapper: {
    position: "relative",
  },
  sizeReservation: {
    opacity: 0,
  },
  overlay: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  // Stretch the overlay's Button to exactly cover the size-reservation
  // Button underneath. `width/height: "100%"` wins over the Button's
  // intrinsic `minWidth: 80` / `buttonMd.height: 44`, and — crucially —
  // keeps the hit target aligned with the original so tap targets are
  // unchanged. Without this the visible spinner-button shrinks to
  // intrinsic size and the trailing portion of longer labels (e.g.
  // "Resume Schedule", or any label with an iconLeft) is uncovered.
  overlayButton: {
    width: "100%",
    height: "100%",
  },
});

/**
 * The alarm control — a single pure function of `AlarmControlView`.
 *
 * Cross-platform: uses only components in `@visioni/a3s-ui/components` that
 * work identically on native and web. No `react-native-reanimated`,
 * `pressto`, or `@visioni/a3s-ui/animations` dependencies, so the Storybook
 * dev server, static build, and `play()` test runner all render the
 * component faithfully without the worklet-initialisation gymnastics the
 * animated `ArmDisarmControl` needs.
 *
 * The render body is one `match(view)...exhaustive()`. No nested ternaries
 * deciding "if armed and not activated and not silenced…" — those conditions
 * collapsed into the variant *before* we rendered. Each branch emits
 * exactly the buttons that variant supports, wired to literal command values.
 */
export const AlarmControl: FC<AlarmControlProps> = ({ view, onCommand, controls }) => {
  const { theme } = useUnistyles();
  const copy = describeView(view);

  // While a command is in flight (`view.kind === "pending"`), we render the
  // *underlying* view and overlay a spinner on the button corresponding to
  // `view.intent`. `pendingFor(kind)` returns truthy when that button is the
  // one the user tapped; everything else stays visually identical but is
  // command-disabled because `interpret` noops during pending.
  //
  // `displayView` unwraps the pending layer for the render body. `copy`
  // works for both pending and non-pending because `describe(pending)`
  // already delegates to `describe(underlying)` — see copy.ts.
  const pending = view.kind === "pending" ? view : null;
  const pendingFor = (intent: PendingIntentKind): boolean => pending?.intent === intent;
  const displayView: AlarmControlView = pending === null ? view : pending.underlying;

  // Wraps the CircularIcon in a -4px-margin View so its visual centre
  // lines up with the title's vertical centre when the surrounding
  // ControlRow is `iconAlign="title"`. (CircularIcon is 32px; title
  // lineHeight is 24px, so the icon needs to be lifted by 4px.)
  const renderLockIcon = (tone: "armed" | "disarmed" | "activated") => {
    const icon = match(tone)
      .with("activated", () => (
        <CircularIcon
          name="warningOutline"
          backgroundColor={theme.colors.status.error}
          borderColor={theme.colors.status.error}
          iconColor={theme.colors.white.default}
        />
      ))
      .with("armed", () => (
        <CircularIcon
          name="lockClosed"
          backgroundColor={theme.colors.purple[600]}
          borderColor={theme.colors.purple[200]}
          iconColor={theme.colors.white.default}
        />
      ))
      .with("disarmed", () => (
        <CircularIcon
          name="lockOpen"
          backgroundColor={theme.colors.warning[600]}
          borderColor={theme.colors.warning[400]}
          iconColor={theme.colors.white.default}
        />
      ))
      .exhaustive();
    return <View style={styles.lockIconWrap}>{icon}</View>;
  };

  // The Adjust row mirrors the legacy `AdjustControl` — a clock icon + a
  // two-line preview of what the schedule will do next, with an "Adjust"
  // button on the right. We render it in every variant where it makes sense
  // (scheduled/inException/inOverride) and the host has enabled `adjust`.
  // Variants where the row doesn't apply return `null` from `describeAdjustRow`.
  // Derived from `displayView` so the row is present (and shows the same
  // preview) during `pending`; the Adjust button itself spins if the pending
  // intent is `adjust`.
  const renderAdjustRow = () => {
    if (!controls.adjust) return null;
    const row = describeAdjustRow(displayView);
    if (row === null) return null;
    // The Adjust button maps to different targets depending on the view:
    // Pick the Adjust target so the command actually does something
    // meaningful for the displayed view:
    //
    // - Finite-end inOverride → extend the active override
    //   (`adjust(activeOverride)`). Pushes `period.end` forward by
    //   `extendBy`; the most natural "I want this override to last
    //   longer" gesture.
    // - Indefinite inOverride → delay the next scheduled transition
    //   (`adjust(nextTransition)`). Extending an indefinite override
    //   would silently shorten it (the interpreter noops to guard
    //   against this, see commands.ts), so the meaningful action is
    //   to anchor a new override at the upcoming flip — same behaviour
    //   as `scheduled` / `inException`.
    // - scheduled / inException / applying → always
    //   `adjust(nextTransition)` because there's no override to
    //   extend.
    const target: "activeOverride" | "nextTransition" =
      displayView.kind === "inOverride" && displayView.override.period.end !== null ?
        "activeOverride"
      : "nextTransition";
    return (
      <ControlRow
        icon={
          <Icon
            name="clockOutline"
            size={CLOCK_ICON_SIZE}
            color={theme.colors.interactive.controls.default_midground}
          />
        }
        title={row.title}
        subtitle={row.subtitle}
        // Adjust-row title (next-transition preview) matches the main
        // row's subtitle weight so the visual hierarchy reads:
        //   main title (md, 16px) > main subtitle (xs, 12px) ≈ adjust
        //   title (xs) > adjust subtitle (xxs, 10px).
        // Bumping from xxs → xs makes the schedule preview legible without
        // overshadowing the main row.
        titleTextStyle="xs/regular"
        subtitleTextStyle="xxs/regular"
        action={
          row.adjustable ?
            <PendingAwareButton
              label={BUTTON_LABELS.adjust}
              size="md"
              variant="primary"
              variantColor="grey"
              pending={pendingFor("adjust")}
              // Adjust row uses default (`iconAlign="center"`) so the
              // button needs no vertical lift — just height.
              style={styles.controlButton}
              onPress={() =>
                onCommand({
                  kind: "adjust",
                  target: { kind: target },
                  extendBy: DEFAULT_EXTEND_BY,
                })
              }
            />
          : undefined
        }
      />
    );
  };

  return (
    <View style={styles.container}>
      {match(displayView)
        .with({ kind: "loading" }, () => (
          <ControlRow
            icon={renderLockIcon("disarmed")}
            title={copy.title}
            titleTextStyle="md/medium"
            titleLineHeight={theme.spacing["4xl"]}
            iconAlign="title"
          />
        ))
        .with({ kind: "error" }, () => (
          <ControlRow
            icon={
              <View style={styles.lockIconWrap}>
                <CircularIcon
                  name="warningOutline"
                  backgroundColor={theme.colors.status.error}
                  borderColor={theme.colors.status.error}
                  iconColor={theme.colors.white.default}
                />
              </View>
            }
            title={copy.title}
            subtitle={copy.subtitle}
            titleTextStyle="md/medium"
            titleLineHeight={theme.spacing["4xl"]}
            subtitleTextStyle="xs/regular"
            iconAlign="title"
          />
        ))
        .with({ kind: "unconfigured" }, () => (
          <ControlRow
            icon={renderLockIcon("disarmed")}
            title={copy.title}
            subtitle={copy.subtitle}
            titleTextStyle="md/medium"
            titleLineHeight={theme.spacing["4xl"]}
            subtitleTextStyle="xs/regular"
            iconAlign="title"
          />
        ))
        .with({ kind: "scheduled" }, (v) => (
          <View style={styles.stack}>
            <ControlRow
              icon={renderLockIcon(v.mode)}
              title={copy.title}
              subtitle={copy.subtitle}
              titleTextStyle="md/medium"
              titleLineHeight={theme.spacing["4xl"]}
              subtitleTextStyle="xs/regular"
              iconAlign="title"
              action={
                controls.override ?
                  <PendingAwareButton
                    label={v.mode === "armed" ? BUTTON_LABELS.disarm : BUTTON_LABELS.arm}
                    size="md"
                    variant="primary"
                    variantColor="blue"
                    pending={pendingFor(v.mode === "armed" ? "disarm" : "arm")}
                    onPress={() =>
                      onCommand(
                        v.mode === "armed" ?
                          { kind: "disarm", duration: DEFAULT_DISARM_DURATION }
                        : { kind: "arm", duration: DEFAULT_ARM_DURATION },
                      )
                    }
                    style={styles.controlButton}
                  />
                : undefined
              }
            />
            {renderAdjustRow()}
          </View>
        ))
        .with({ kind: "inException" }, (v) => (
          <View style={styles.stack}>
            <ControlRow
              icon={renderLockIcon(v.mode)}
              title={copy.title}
              subtitle={copy.subtitle}
              titleTextStyle="md/medium"
              titleLineHeight={theme.spacing["4xl"]}
              subtitleTextStyle="xs/regular"
              iconAlign="title"
              action={
                controls.override ?
                  <PendingAwareButton
                    label={v.mode === "armed" ? BUTTON_LABELS.disarm : BUTTON_LABELS.arm}
                    size="md"
                    variant="primary"
                    variantColor="blue"
                    pending={pendingFor(v.mode === "armed" ? "disarm" : "arm")}
                    onPress={() =>
                      onCommand(
                        v.mode === "armed" ?
                          { kind: "disarm", duration: DEFAULT_DISARM_DURATION }
                        : { kind: "arm", duration: DEFAULT_ARM_DURATION },
                      )
                    }
                    style={styles.controlButton}
                  />
                : undefined
              }
            />
            {renderAdjustRow()}
          </View>
        ))
        .with({ kind: "inOverride" }, (v) => (
          <View style={styles.stack}>
            <ControlRow
              icon={renderLockIcon(v.mode)}
              title={copy.title}
              subtitle={copy.subtitle}
              titleTextStyle="md/medium"
              titleLineHeight={theme.spacing["4xl"]}
              subtitleTextStyle="xs/regular"
              iconAlign="title"
              action={
                controls.override ?
                  <PendingAwareButton
                    label={BUTTON_LABELS.resume}
                    size="md"
                    variant="primary"
                    variantColor="blue"
                    pending={pendingFor("resume")}
                    onPress={() => onCommand({ kind: "resume" })}
                    style={styles.controlButton}
                  />
                : undefined
              }
            />
            {renderAdjustRow()}
          </View>
        ))
        .with({ kind: "activated" }, (v) => (
          <View style={styles.stack}>
            <ControlRow
              icon={renderLockIcon("activated")}
              title={copy.title}
              subtitle={copy.subtitle}
              titleTextStyle="md/medium"
              titleLineHeight={theme.spacing["4xl"]}
              subtitleTextStyle="xs/regular"
              iconAlign="title"
            />
            {controls.silence && (
              <View style={styles.silenceRow}>
                <PendingAwareButton
                  label={v.silence.kind === "active" ? BUTTON_LABELS.extendSilence : BUTTON_LABELS.silence}
                  size="md"
                  variant="primary"
                  variantColor="red"
                  iconLeft={<Icon name="alarmPause" size={ICON_SIZE} color={theme.colors.white.default} />}
                  pending={pendingFor(v.silence.kind === "active" ? "extendSilence" : "silence")}
                  onPress={() =>
                    onCommand(
                      v.silence.kind === "active" ?
                        { kind: "extendSilence", minutes: DEFAULT_SILENCE_MINUTES }
                      : { kind: "silence", minutes: DEFAULT_SILENCE_MINUTES },
                    )
                  }
                />
              </View>
            )}
          </View>
        ))
        .with({ kind: "applying" }, (v) => (
          // Two-row stack mirroring the other action-bearing variants.
          // The now row is a bespoke layout (because ControlRow doesn't
          // compose icons into the subtitle and we want the green
          // spinning sync icon next to "Applying changes"). The Adjust
          // row underneath lets the user re-target the upcoming
          // transition while the previous change is still propagating —
          // pressing Adjust here creates a fresh override anchored at
          // the configured schedule's next flip.
          <View style={styles.stack}>
            <View style={styles.applyingRow}>
              {/*
                Lock icon is driven directly by `currMode` — *not* by
                `direction` — so a camera that is armed and disarming
                stays purple/locked until the change actually lands.
                `Mode` is structurally a subset of `renderLockIcon`'s
                tone alphabet, so this is a total mapping with no
                conditional translation.
              */}
              <View style={styles.applyingIcon}>{renderLockIcon(v.currMode)}</View>
              <View style={styles.applyingTextStack}>
                <Text textStyle="md/medium" color="text" style={{ lineHeight: theme.spacing["4xl"] }}>
                  {copy.title}
                </Text>
                <View style={styles.applyingSubtitleRow}>
                  <SpinEffect>
                    <Icon name="cycle" size={14} color={theme.colors.status.success} />
                  </SpinEffect>
                  <Text textStyle="xs/regular" color="textMuted">
                    {copy.subtitle}
                  </Text>
                </View>
              </View>
            </View>
            {renderAdjustRow()}
          </View>
        ))
        // The `pending` case is unreachable here: `displayView` was narrowed
        // to `view.underlying` at the top of the render when a command is
        // in flight, so we never enter the match with `kind: "pending"`.
        // The exhaustive check still requires a branch.
        .with({ kind: "pending" }, () => null)
        .exhaustive()}
    </View>
  );
};

const styles = StyleSheet.create((theme) => ({
  container: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.border,
    borderWidth: 1,
    borderRadius: theme.spacing.lg,
    paddingVertical: theme.spacing.lg,
    paddingHorizontal: theme.spacing["3xl"],
    alignSelf: "stretch",
  },
  stack: {
    // Bigger gap between the main row and the Adjust row so the rows
    // don't visually crowd each other.
    gap: theme.spacing.lg,
  },
  silenceRow: {
    alignSelf: "stretch",
  },
  // Compact button height for in-card action buttons. Slightly taller
  // than the 32px CircularIcon so the primary blue button has a
  // comfortable tap target, but visually lighter than the default 44px.
  // The Button primitive's intrinsic `minWidth: 80` is preserved so
  // labels stay tappable without width jitter between rows.
  //
  // Token: 9xl (36px). Used for every action button (Disarm / Arm /
  // Resume / Adjust).
  //
  // ControlRow's action slot is itself centred on the full row (see
  // `ControlRow.tsx`'s `styles.action`), so the button only needs its
  // height locked here. Both main-row and Adjust-row buttons share this
  // style so they render as identical pills.
  controlButton: {
    height: theme.spacing["9xl"],
  },
  // The lock icon (CircularIcon, 32px = 8xl) is taller than its title
  // line (overridden to 5xl = 20px on every now-row title), so its
  // centre needs to lift up by half the difference:
  //   (32 - 18) / 2 = 7, rounded to `sm` (6) since there's no 7px token.
  lockIconWrap: {
    marginTop: -theme.spacing.sm,
  },
  // Custom layout for the `applying` variant — mirrors ControlRow's
  // proportions (lock icon left, title/subtitle stack) but adds a
  // sub-row in the subtitle slot to host the green sync spinner.
  applyingRow: {
    flexDirection: "row",
    // Top-align so the lock icon sits with the title (matching the
    // `iconAlign="title"` behaviour of the other variants).
    alignItems: "flex-start",
    gap: theme.spacing.md,
  },
  applyingIcon: {
    alignItems: "center",
    justifyContent: "center",
    // No margin here — the inner `renderLockIcon` already wraps with
    // `lockIconWrap` (-6px marginTop) so the icon's centre lands on the
    // title-line centre.
  },
  applyingTextStack: {
    flexShrink: 1,
    minWidth: 0,
  },
  applyingSubtitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
}));
